// Tenant + branch context.
// Tenant определяется по subdomain (см. docs/stack/07-frontend-vite.md §3); branch — селектором в AppShell.
// Backend всё равно валидирует tenant_id из JWT — фронт нужен для query keys и брендирования.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface TenantContextValue {
  tenantSlug: string | null;
  tenantId: string | null;
  /**
   * Выбранные в шапке филиалы (план 106, мультивыбор). ПУСТОЙ массив = «Все
   * филиалы» — та же договорённость, что на сервере, где пустой скоуп значит
   * «без ограничений».
   */
  branchIds: string[];
  /**
   * Пользователь уже делал явный выбор филиала (в т.ч. «Все филиалы»).
   * Нужно, чтобы дефолт по филиалу сотрудника подставлялся только один раз и не
   * затирал осознанно выбранные «Все филиалы» при каждой перезагрузке.
   */
  branchChosen: boolean;
  /**
   * Филиалы в шапке выбраны РУКАМИ (план 157). В отличие от `branchChosen`,
   * не ставится ни автоподстановкой филиала сотрудника (`setDefaultBranch`),
   * ни чтением сохранённого списка: сохранённый список неотличим от
   * автоподставленного. Живёт в localStorage отдельным ключом.
   *
   * Нужен умолчанию филиала нового клиента: автоматически подставленная шапка
   * — «первый филиал сотрудника», а не его решение, и доверять ей нельзя.
   */
  branchPickedByUser: boolean;
  setTenant: (tenantId: string, slug?: string) => void;
  setBranches: (branchIds: string[]) => void;
  /** Подставить дефолт, не помечая выбор сделанным (см. `branchChosen`). */
  setDefaultBranch: (branchId: string) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Ключ мультивыбора (план 106). НОВЫЙ, а не переиспользованный старый: откат на
 * предыдущую сборку прочитал бы JSON-массив как uuid филиала и молча сломал
 * выбор. Старый ключ читается один раз при миграции и удаляется.
 */
const BRANCHES_STORAGE_KEY = 'dance-crm:active-branches';
const LEGACY_BRANCH_KEY = 'dance-crm:active-branch';

/** Сентинел «Все филиалы» в localStorage — отличает явный выбор от «не выбирал». */
const ALL_BRANCHES = 'all';

/** Признак «шапку выбирали руками» (план 157) — см. `branchPickedByUser`. */
const PICKED_BY_USER_KEY = 'dance-crm:branches-picked-by-user';

function readPickedByUser(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(PICKED_BY_USER_KEY) === '1';
}

/**
 * Что лежит в хранилище: список филиалов, «все» либо ничего (не выбирал).
 *
 * Читает и НОВЫЙ ключ, и старый одиночный — второй переносит и стирает. Без
 * переноса каждый сотрудник после выката получил бы «Все филиалы» вместо
 * своего, и первым же действием увидел бы чужой город.
 */
function readStoredBranches(): { ids: string[]; chosen: boolean } {
  if (typeof window === 'undefined') return { ids: [], chosen: false };
  const stored = window.localStorage.getItem(BRANCHES_STORAGE_KEY);
  if (stored === ALL_BRANCHES) return { ids: [], chosen: true };
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return { ids: parsed.filter((v): v is string => typeof v === 'string'), chosen: true };
      }
    } catch {
      // Битое значение — ведём себя как «не выбирал»: дефолт подставится сам.
    }
    return { ids: [], chosen: false };
  }

  const legacy = window.localStorage.getItem(LEGACY_BRANCH_KEY);
  if (legacy === null) return { ids: [], chosen: false };
  window.localStorage.removeItem(LEGACY_BRANCH_KEY);
  return legacy === ALL_BRANCHES ? { ids: [], chosen: true } : { ids: [legacy], chosen: true };
}

/**
 * Извлекает tenant slug из текущего hostname.
 * Примеры:
 *   myschool.app.ru → "myschool"
 *   localhost       → null
 *   app.ru          → null (apex)
 */
export function parseTenantSlug(hostname: string): string | null {
  if (!hostname || hostname === 'localhost' || /^[\d.]+$/.test(hostname)) {
    return null;
  }
  const parts = hostname.split('.');
  if (parts.length < 3) {
    return null;
  }
  const slug = parts[0];
  if (!slug || slug === 'www' || slug === 'app') {
    return null;
  }
  return slug;
}

export interface TenantProviderProps {
  children: ReactNode;
  initialTenantId?: string | null;
  initialBranchId?: string | null;
}

export function TenantProvider({
  children,
  initialTenantId = null,
  initialBranchId = null,
}: TenantProviderProps): JSX.Element {
  const tenantSlug = useMemo(
    () => (typeof window !== 'undefined' ? parseTenantSlug(window.location.hostname) : null),
    [],
  );
  const [tenantId, setTenantId] = useState<string | null>(initialTenantId);
  const stored = useMemo(() => readStoredBranches(), []);
  const [branchIds, setBranchIds] = useState<string[]>(() =>
    initialBranchId ? [initialBranchId] : stored.ids,
  );
  const [branchChosen, setBranchChosen] = useState<boolean>(() =>
    initialBranchId ? true : stored.chosen,
  );
  const [branchPickedByUser, setBranchPickedByUser] = useState<boolean>(() =>
    initialBranchId ? true : readPickedByUser(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (branchIds.length > 0) {
      window.localStorage.setItem(BRANCHES_STORAGE_KEY, JSON.stringify(branchIds));
    } else if (branchChosen) {
      // Явно выбранные «Все филиалы» — запоминаем сентинелом, иначе при следующем
      // входе сотруднику снова подставится его филиал по умолчанию.
      window.localStorage.setItem(BRANCHES_STORAGE_KEY, ALL_BRANCHES);
    } else {
      window.localStorage.removeItem(BRANCHES_STORAGE_KEY);
    }
  }, [branchIds, branchChosen]);

  const setTenant = useCallback((nextTenantId: string) => {
    setTenantId(nextTenantId);
  }, []);

  const setBranches = useCallback((next: string[]) => {
    setBranchIds(next);
    setBranchChosen(true);
    setBranchPickedByUser(true);
    if (typeof window !== 'undefined') window.localStorage.setItem(PICKED_BY_USER_KEY, '1');
  }, []);

  /** Подстановка дефолта (филиал сотрудника) — не считается выбором пользователя. */
  const setDefaultBranch = useCallback((nextBranchId: string) => {
    setBranchIds([nextBranchId]);
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenantSlug,
      tenantId,
      branchIds,
      branchChosen,
      branchPickedByUser,
      setTenant,
      setBranches,
      setDefaultBranch,
    }),
    [
      tenantSlug,
      tenantId,
      branchIds,
      branchChosen,
      branchPickedByUser,
      setTenant,
      setBranches,
      setDefaultBranch,
    ],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within <TenantProvider>');
  }
  return ctx;
}

export function useTenantId(): string | null {
  return useTenant().tenantId;
}

/**
 * Выбранные филиалы для ФИЛЬТРОВ списков (план 106). Пустой массив = все.
 *
 * Пришёл на смену `useBranchId`, у которого была двойная роль — фильтр И
 * дефолт формы. При мультивыборе эти роли расходятся: список показывает все
 * выбранные филиалы, а форма создания не может выбрать один за человека.
 */
export function useBranchIds(): string[] {
  return useTenant().branchIds;
}

/**
 * Филиал для ДЕФОЛТА ФОРМЫ создания (план 106, решение владельца 26.08).
 *
 * Возвращает id, только когда выбран ровно один филиал. При 0 или 2+ — `null`:
 * поле «Филиал» остаётся пустым и обязательным, потому что записать клиента,
 * продать абонемент или открыть смену не в том филиале дороже лишнего клика.
 */
export function useDefaultFormBranchId(): string | null {
  const ids = useTenant().branchIds;
  return ids.length === 1 ? ids[0]! : null;
}

/**
 * Канонический ключ выбора филиалов для React Query.
 *
 * Сортировка обязательна: один и тот же набор, отмеченный в разном порядке,
 * иначе даёт разные ключи кэша и дубли запросов.
 */
export function branchQueryKey(ids: string[]): string {
  return ids.length === 0 ? 'all' : [...ids].sort().join(',');
}
